import React from 'react'
import PropTypes from 'prop-types'
import { classNames, Icon, iconProps } from '@zambezi/sdk/common'
import PagerForm from './PagerForm'

/**
 * Create a React Pager component
 *
 * @param {import('./interfaces').PagerProps} props
 * @returns {JSX.Element}
 */
const Pager = ({
  pageSize,
  offset: propOffset,
  total,
  itemsToShow,
  anchorHrefFn,
  withFirst,
  withLast,
  withForm,
  withPagesText,
  accessibilityLabel,
  onGoto,
  anchorPreventDefault,
  children,
  prevIconProps,
  nextIconProps
}) => {
  const hasMax = total >= 0 && total < Number.MAX_VALUE
  const offset = Math.min(
    hasMax ? total : Number.MAX_VALUE,
    Math.max(0, Math.floor(propOffset / pageSize) * pageSize)
  )
  const handleClick = (offset, event) => {
    onGoto(offset, event)
    if (anchorPreventDefault) event.preventDefault()
  }

  return (
    <nav className='zb-pager' role='navigation' aria-label={accessibilityLabel}>
      {children}
      {renderPreItems()}
      {itemsToShow ? <ul>{renderPages()}</ul> : renderPagesText() || null}
      {renderPostItems()}
      {itemsToShow ? renderPagesText() : null}
      {withForm ? (
        <PagerForm
          hasMax={hasMax}
          pageSize={pageSize}
          total={total}
          onSubmit={handleClick}
          anchorHrefFn={anchorHrefFn}
        />
      ) : null}
    </nav>
  )

  function renderPagesText () {
    const currentPage = total ? Math.floor(offset / pageSize) + 1 : 0
    const totalPages = hasMax
      ? ` of ${Math.floor((total - 1) / pageSize) + 1}`
      : null
    return (
      <span
        className={withPagesText ? 'zb-pager-text' : 'zb-pager-sr-only'}
        aria-live='polite'
      >
        Page {currentPage}
        {totalPages}
      </span>
    )
  }

  function renderPreItems () {
    const linkOffset = Math.max(offset - pageSize, 0)
    const isDisabled = offset === 0
    const lastPage = getLastPage()

    return [
      withFirst && !isDisabled ? (
        <a
          key='first'
          href={anchorHrefFn(0)}
          onClick={e => handleClick(0, e)}
          title='Go to first page'
          aria-label={getGotoLabel(0, lastPage)}
          className='zb-pager-navigation zb-pager-first'
        >
          first
        </a>
      ) : null,
      <a
        key='previous'
        href={isDisabled ? null : anchorHrefFn(linkOffset)}
        onClick={isDisabled ? null : e => handleClick(linkOffset, e)}
        role={'button'}
        title='Go to previous page'
        aria-label={'Go to previous page'}
        aria-disabled={isDisabled}
        className={classNames('zb-pager-navigation zb-pager-previous', {
          'zb-pager-navigation-is-disabled': isDisabled
        })}
        tabIndex='0'
      >
        <Icon
          name='core-pager-prev'
          aria-hidden
          iconProps={prevIconProps}
          title='Go to previous page'
        />
      </a>
    ]
  }

  function renderPostItems () {
    const linkOffset = offset + pageSize
    const isDisabled = hasMax && offset >= total - pageSize
    const hasLastItem = withLast && hasMax && !isDisabled
    const lastOffset = Math.floor((total - 1) / pageSize) * pageSize
    const lastPage = getLastPage()

    return [
      <a
        key='next'
        href={isDisabled ? null : anchorHrefFn(linkOffset)}
        onClick={isDisabled ? null : e => handleClick(linkOffset, e)}
        role={'button'}
        title='Go to next page'
        aria-label={'Go to next page'}
        aria-disabled={isDisabled}
        className={classNames('zb-pager-navigation zb-pager-next', {
          'zb-pager-navigation-is-disabled': isDisabled
        })}
        tabIndex='0'
      >
        <Icon
          name='core-pager-next'
          aria-hidden
          iconProps={nextIconProps}
          title='Go to next page'
        />
      </a>,
      hasLastItem ? (
        <a
          key='last'
          href={anchorHrefFn(lastOffset)}
          onClick={e => handleClick(lastOffset, e)}
          title='Go to last page'
          aria-label={getGotoLabel(lastPage, lastPage)}
          className='zb-pager-navigation zb-pager-last'
        >
          last
        </a>
      ) : null
    ]
  }

  function renderPages () {
    const lastPage = getLastPage()
    const bounds = Math.max(0, Math.ceil((itemsToShow - 1) / 2))
    const offsetPage = Math.max(0, Math.ceil(offset / pageSize))
    const minPage = Math.max(
      0,
      Math.min(offsetPage - bounds, lastPage - itemsToShow + 1)
    )
    const maxPage = Math.min(
      lastPage,
      Math.max(offsetPage + bounds, itemsToShow - 1)
    )
    const items = []

    if (minPage > 0) {
      items.push(
        <li key={0}>
          <span className='zb-pager-ellipsis'>&hellip;</span>
        </li>
      )
    }

    for (let currentPage = minPage; currentPage <= maxPage; currentPage += 1) {
      let isActive = currentPage === offsetPage
      let currentOffset = currentPage * pageSize

      items.push(
        <li key={currentPage}>
          <a
            href={anchorHrefFn(currentOffset)}
            onClick={e => handleClick(currentOffset, e)}
            aria-current={isActive ? true : null}
            className={classNames('zb-pager-navigation zb-pager-item', {
              'zb-pager-navigation-is-active': isActive
            })}
            data-pager-page={currentPage + 1}
            data-pager-offset={currentOffset}
            aria-label={getGotoLabel(currentPage, lastPage)}
          >
            {currentPage + 1}
          </a>
        </li>
      )
    }

    if (maxPage !== lastPage) {
      items.push(
        <li key={lastPage}>
          <span className='zb-pager-ellipsis'>&hellip;</span>
        </li>
      )
    }

    return items
  }

  function getLastPage () {
    const top = hasMax ? total - 1 : offset + pageSize
    return Math.floor(top / pageSize)
  }
}

/**
 * @param {number} page
 * @param {number} lastPage
 */
function getGotoLabel (page, lastPage) {
  return `Go to page ${page + 1} of ${lastPage + 1}`
}

Pager.defaultProps = {
  total: Number.POSITIVE_INFINITY,
  itemsToShow: 5,
  pageSize: 1,
  offset: 0,
  anchorHrefFn: offset => `#`,
  anchorPreventDefault: true,
  withFirst: false,
  withLast: false,
  accessibilityLabel: 'Pagination'
}

Pager.propTypes = {
  offset: PropTypes.number,
  pageSize: PropTypes.number,
  total: PropTypes.number,
  itemsToShow: PropTypes.number,
  anchorHrefFn: PropTypes.func,
  onGoto: PropTypes.func.isRequired,
  anchorPreventDefault: PropTypes.bool,
  withFirst: PropTypes.bool,
  withLast: PropTypes.bool,
  withPagesText: PropTypes.bool,
  withForm: PropTypes.bool,
  accessibilityLabel: PropTypes.string,
  prevIconProps: iconProps,
  nextIconProps: iconProps
}

export default Pager
